
import React, { useState } from 'react';
import { DailyChecklistStatus } from '../types';

interface TokenDisplayProps {
  tokens: number;
  tokenIcon: string;
  onSpendToken: (taskKey: string) => void;
  dailyChecklistCompleted: DailyChecklistStatus;
}

const TokenDisplay: React.FC<TokenDisplayProps> = ({ tokens, tokenIcon, onSpendToken, dailyChecklistCompleted }) => {
  const [readyToSpend, setReadyToSpend] = useState<string | null>(null);

  const allChecklistItems = [
    { key: 'desk', text: 'Is my creative desk tidy? If not, spend 3 minutes tidying it.', daily: false },
    { key: 'clothes', text: 'Are any dirty clothes on my floor? If so, pick them up and put them in the washbasket.', daily: true },
    { key: 'bed', text: 'Is my bed made? If not, tidy up my blanket.', daily: true },
    { key: 'dressed', text: 'Am I dressed? If not, get dressed.', daily: true },
  ];

  const availableItems = allChecklistItems.filter(item => {
    if (!item.daily) return true;
    return !dailyChecklistCompleted[item.key as keyof typeof dailyChecklistCompleted];
  });

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl p-6 flex flex-col md:flex-row items-center justify-between shadow-lg">
      <div className="flex-grow">
        <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Game Tokens</h2>
        <p className="text-slate-600 dark:text-slate-400">Your currency for Game Mode time. 1 Token = 60 minutes.</p>
        <p className="text-slate-600 dark:text-slate-400 mt-2 text-sm max-w-prose">
          Game time is a fun way to relax and explore amazing digital worlds. Spending a token is a choice to power up your games, while making sure there's still plenty of time for all the other awesome real-world adventures. It's all about finding a great balance!
        </p>
        <div className="flex items-center mt-4 space-x-1">
          {Array.from({ length: tokens }).map((_, index) => (
            <span key={index} className="text-4xl drop-shadow-[0_2px_2px_rgba(0,0,0,0.2)]" style={{ lineHeight: 1 }}>{tokenIcon}</span>
          ))}
          {tokens === 0 && <p className="text-slate-500 italic pl-2">No tokens remaining for today.</p>}
        </div>
      </div>
      <div className="mt-4 md:mt-0 w-full md:w-auto md:max-w-xs lg:max-w-sm">
        {tokens > 0 ? (
          readyToSpend ? (
            <button
              onClick={() => {
                if (readyToSpend) {
                  onSpendToken(readyToSpend);
                }
                setReadyToSpend(null);
              }}
              className="w-full px-6 py-3 bg-red-600 text-white font-bold rounded-lg shadow-md hover:bg-red-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-75"
            >
              Spend 1 Game Token
            </button>
          ) : (
            <div className="w-full text-left bg-slate-100 dark:bg-slate-700/50 p-4 rounded-lg">
              <div role="radiogroup" aria-labelledby="checklist-heading">
                <h3 id="checklist-heading" className="font-bold text-slate-800 dark:text-slate-200 mb-3">First, a quick check:</h3>
                <ul className="space-y-3">
                  {availableItems.map((item) => (
                    <li key={item.key} className="flex items-start">
                      <div className="flex items-center h-5">
                          <input
                            id={item.key}
                            name="pre-spend-task"
                            type="radio"
                            onChange={() => setReadyToSpend(item.key)}
                            className="focus:ring-primary-500 h-4 w-4 text-primary-600 border-slate-300 dark:border-slate-500 bg-white dark:bg-slate-700 rounded cursor-pointer"
                            style={{'--tw-ring-color': 'var(--primary-500)' } as React.CSSProperties}
                          />
                      </div>
                      <div className="ml-3 text-sm">
                          <label htmlFor={item.key} className="font-medium text-slate-700 dark:text-slate-300 cursor-pointer">
                            {item.text}
                          </label>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )
        ) : (
          <button
            disabled
            className="w-full px-6 py-3 bg-slate-300 dark:bg-slate-700 disabled:cursor-not-allowed disabled:text-slate-500 dark:disabled:text-slate-500 font-bold rounded-lg"
          >
            No Tokens Left
          </button>
        )}
      </div>
    </div>
  );
};

export default TokenDisplay;
